<script setup lang="ts">
  import { computed, ref } from 'vue';

  import type { Partner } from '@stuut/api/models';
  import { formatPhoneNumber } from '@stuut/common/src/utils';
  import { CopyButton } from '@ui/components/button';
  import { ToggleGroup, ToggleGroupItem } from '@ui/components/toggle-group';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import ContactCards from '@/components/contacts/ContactCards.vue';
  import RetellLanguageSelector from '@/components/partners/RetellLanguageSelector.vue';

  interface Props {
    partner: Partner;
  }

  const props = defineProps<Props>();

  const currentTab = ref<'external' | 'internal'>('external');
  const partnerContactsRef = ref();

  const externalContacts = computed(() =>
    props.partner.allContacts.filter((c) => !c.internal_role),
  );
  const internalContacts = computed(() => props.partner.allContacts.filter((c) => c.internal_role));

  defineExpose({
    openContactEditor: () => {
      partnerContactsRef.value?.openContactEditor();
    },
  });
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-4 w-full text-xs">
      <div class="grid grid-cols-2 items-center justify-end gap-x-4 truncate">
        <span class="text-muted-foreground">Primary Email</span>

        <Tooltip v-if="partner.primaryEmail">
          <CopyButton
            :copy-value="partner.primaryEmail"
            class="px-0 justify-end"
            variant="transparent"
            size="sm"
          >
            <div class="flex items-center justify-end gap-1.5 truncate">
              <TooltipTrigger as-child>
                <span class="truncate">
                  {{ partner.primaryEmail }}
                </span>
              </TooltipTrigger>

              <i class="far fa-copy text-xs text-muted-foreground"></i>
            </div>
          </CopyButton>

          <TooltipContent side="right">{{ partner.primaryEmail }}</TooltipContent>
        </Tooltip>

        <template v-else>
          <i class="text-xs text-error fa-solid fa-exclamation-circle"></i>
          <span class="text-muted-foreground">Missing</span>
        </template>
      </div>

      <div class="grid grid-cols-2 items-center justify-end gap-x-4 truncate">
        <span class="text-muted-foreground">Primary Phone</span>

        <Tooltip v-if="partner.primaryPhone">
          <CopyButton
            :copy-value="partner.primaryPhone"
            class="px-0 justify-end"
            variant="transparent"
            size="sm"
          >
            <div class="flex items-center justify-end gap-1.5 truncate">
              <TooltipTrigger as-child>
                <span class="truncate">
                  {{ formatPhoneNumber(partner.primaryPhone) }}
                </span>
              </TooltipTrigger>

              <i class="far fa-copy text-xs text-muted-foreground"></i>
            </div>
          </CopyButton>

          <TooltipContent side="right">{{
            formatPhoneNumber(partner.primaryPhone)
          }}</TooltipContent>
        </Tooltip>

        <template v-else>
          <i class="text-xs text-error fa-solid fa-exclamation-circle"></i>
          <span class="text-muted-foreground">Missing</span>
        </template>
      </div>

      <div class="flex items-center justify-between gap-x-4 truncate">
        <span class="text-muted-foreground">Language</span>

        <div class="flex items-center gap-2 truncate">
          <RetellLanguageSelector :partner="partner" />
        </div>
      </div>
    </div>

    <ToggleGroup v-model="currentTab" class="w-full" variant="outline">
      <ToggleGroupItem value="external" class="flex-1"> Customer </ToggleGroupItem>

      <ToggleGroupItem value="internal" class="flex-1"> Internal </ToggleGroupItem>
    </ToggleGroup>

    <ContactCards
      ref="partnerContactsRef"
      :partner
      :contacts="currentTab === 'external' ? externalContacts : internalContacts"
      :mode="currentTab"
    />
  </div>
</template>

<style scoped></style>
